import { AlertTriangle, CheckIcon, ChevronsDown, ChevronsUp, ExternalLink, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import NeuhausChocolate from '../../assets/Chocolate/Neuhaus_chocolate.webp';
import FictionalChocolate from '../../assets/chocolate-fictional/fictional-choco.jpg';
import WipotechChocolate from '../../assets/Wipo-choco/h2wipo-choco.jpg';
import MuncheeImage from '../../assets/munchee-tikiri-marie/Munchee-Tiki Marie.jpg';
import ShanTeasImage from '../../assets/shan-teas/shan-teas.jpg';
import HemasClogardImage from '../../assets/hemas-clogard/Hemas-Clogard.jpg';
import FairtradeLogo from '../../assets/chocolate-fictional/fairtrade-light.png';
import QRImage from '../../assets/LinqOne/linqOnePhone.png';
import BottomNavbar from '../../layouts/BottomNavbar';
import TopBar from '../../layouts/TopBar';
import MenuSidebar from '../../layouts/MenuSidebar';
import { darkTheme, lightTheme } from '../../theme/theme';
import { getActiveProduct, getProductDataFolder, PRODUCTS, getVerificationStatusFromHash, getVerificationState } from '../../utils/urlConfig';
import useTheme from '../../hooks/useTheme';

const VerificationScreen = ({ hashFragment, productKey, urlSerialNumber: urlSerialNumberProp } = {}) => {
  const location = useLocation();
  const { id: verificationId, serialNumber: urlSerialNumberParam, productId: urlProductId } = useParams();
  const urlSerialNumber = urlSerialNumberProp || urlSerialNumberParam;
  const [isDark, setIsDark] = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [productData, setProductData] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  // When rendered via ProductResolver, productKey is the already-resolved product id
  // passed synchronously as a prop — no localStorage read needed on first render.
  // When navigated to directly via QR scan, resolve from productId URL param.
  // Fall back to localStorage as last resort.
  const resolveProductFromUrl = () => {
    if (productKey) return productKey;
    if (urlProductId) {
      for (const [key, config] of Object.entries(PRODUCTS)) {
        if (config.gtin === urlProductId || (config.gtins && config.gtins.includes(urlProductId))) return key;
      }
    }
    return getActiveProduct();
  };
  const [activeProduct, setActiveProductState] = useState(resolveProductFromUrl);

  const [verificationStatus, setVerificationStatus] = useState('verified');
  const [verificationState, setVerificationState]   = useState(null);
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const navigate = useNavigate();
  const theme = isDark ? darkTheme : lightTheme;

  // Derived from activeProduct — updated whenever activeProduct state changes
  const productConfig = PRODUCTS[activeProduct];
  const imageMap = {
    'QR.png': QRImage,
    'Neuhaus_chocolate.webp': NeuhausChocolate,
    'fictional-choco.jpg': FictionalChocolate,
    'h2wipo-choco.jpg': WipotechChocolate,
    'Munchee-Tiki Marie.jpg': MuncheeImage,
    'Hemas-Clogard.jpg': HemasClogardImage,
    'shan-teas.jpg': ShanTeasImage,
  };
  const authenticatedImage = imageMap[productConfig?.authenticatedImage] || QRImage;

  // If productKey prop changes (user navigates from one product route to another
  // without unmounting this component), keep activeProduct in sync.
  useEffect(() => {
    if (productKey) setActiveProductState(productKey);
  }, [productKey]);

  // Resolve verification status from URL params or hash
  // Priority: URL :id param → explicit prop (from ProductResolver) → location.hash → localStorage → window.location.hash
  useEffect(() => {
    // Resolve product from URL productId param when productKey prop is not available.
    if (!productKey) {
      if (urlProductId) {
        for (const [key, config] of Object.entries(PRODUCTS)) {
          if (config.gtin === urlProductId || (config.gtins && config.gtins.includes(urlProductId))) {
            setActiveProductState(key);
            break;
          }
        }
      } else {
        setActiveProductState(getActiveProduct());
      }
    }

    // If verificationId from URL param exists, use it directly (e.g., "verified1", "suspected", "expired")
    if (verificationId) {
      const state = getVerificationState(verificationId);
      setVerificationStatus(verificationId);
      setVerificationState(state);
      // Store the current verification status and serial for later retrieval
      localStorage.setItem('lastVerificationStatus', verificationId);
      if (urlSerialNumber) localStorage.setItem('lastVerificationSerial', urlSerialNumber);
      return;
    }

    // Fall back to hash-based resolution
    const hash = hashFragment !== undefined
      ? hashFragment
      : (location.hash || window.location.hash);

    const status = getVerificationStatusFromHash(hash);
    const state  = getVerificationState(status);

    setVerificationStatus(status);
    setVerificationState(state);
    // Store the current verification status and serial for later retrieval
    if (status) {
      localStorage.setItem('lastVerificationStatus', status);
      if (urlSerialNumber) localStorage.setItem('lastVerificationSerial', urlSerialNumber);
    }
  }, [location, hashFragment, productKey, verificationId, urlSerialNumber, urlProductId]);

  useEffect(() => {
    // Load product identification data
    const loadProductData = async () => {
      try {
        const dataFolder = getProductDataFolder(activeProduct);
        const data = await import(`../../data/${dataFolder}/product_identification.json`);
        setProductData({ content: data.content, serialOverrides: data.serialOverrides || {} });
      } catch (error) {
        console.error('Error loading product data:', error);
      }
    };

    loadProductData();
  }, [activeProduct, location]);

  // Track scroll position
  useEffect(() => {
    const scrollContainer = document.querySelector('.overflow-y-auto');
    if (!scrollContainer) return;

    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      const scrollHeight = scrollContainer.scrollHeight;
      const clientHeight = scrollContainer.clientHeight;
      
      // Consider scrolled down if we're past 50% of scrollable area
      const isAtBottom = scrollTop > (scrollHeight - clientHeight) / 2;
      setIsScrolledDown(isAtBottom);
    };

    scrollContainer.addEventListener('scroll', handleScroll);
    return () => scrollContainer.removeEventListener('scroll', handleScroll);
  }, []);

  // Get theme colors based on verification status
  const getStatusColors = () => {
    switch (verificationStatus) {
      case 'verified':
        return {
          bg: theme.labelbg,
          stroke: theme.labelstroke,
          text: theme.textgreen,
          iconBg: theme.greenbg
        };
      case 'suspected':
        return {
          bg: theme.suspectedbg,
          stroke: theme.suspectedstroke,
          text: theme.textsuspected,
          iconBg: theme.suspectedlabel
        };
      case 'expired':
        return {
          bg: theme.expiredbg,
          stroke: theme.expiredstroke,
          text: theme.textexpired,
          iconBg: theme.expiredlabel
        };
      default:
        return {
          bg: theme.labelbg,
          stroke: theme.labelstroke,
          text: theme.textgreen,
          iconBg: theme.greenbg
        };
    }
  };

  // Get label text based on verification status
  const getStatusLabel = () => {
    switch (verificationStatus) {
      case 'verified':
        return 'Authentic';
      case 'suspected':
        return 'Suspected';
      case 'expired':
        return 'Expired';
      default:
        return 'Authentic';
    }
  };

  // Get icon based on verification status
  const getStatusIcon = () => {
    switch (verificationStatus) {
      case 'verified':
        return <CheckIcon size={24} className="text-white" />;
      case 'suspected':
        return <AlertTriangle size={24} className="text-white" />;
      case 'expired':
        return <AlertTriangle size={24} className="text-white" />;
      default:
        return <CheckIcon size={24} className="text-white" />;
    }
  };

  return (
    <div
      className="h-screen flex flex-col"
      style={{ backgroundColor: theme.background1 }}
    >
      {/* Header */}
      <TopBar
        theme={theme}
        isDark={isDark}
        setIsDark={setIsDark}
        title="Verification"
        onBackClick={() => navigate('/scanqr', { state: { isDark } })}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
      />

      {/* Menu Sidebar */}
      <MenuSidebar
        theme={theme}
        isDark={isDark}
        isOpen={isMenuOpen}
        onClose={() => setIsMenuOpen(false)}
      />

      {/* Main Content - Scrollable */}
      <div className="flex-1 overflow-y-auto px-4 pt-20 pb-32">
        <div className="flex flex-col gap-4 w-full">
          {/* Authenticated Image */}
          <div
            className="flex flex-col items-center justify-center text-center w-full h-auto rounded-lg overflow-hidden"
            style={{ backgroundColor: theme.background2, borderColor: theme.stroke, borderWidth: 1 }}
          >
            <div className="flex flex-col items-center justify-center text-center w-full max-w-md h-auto relative">
              <div className="absolute top-2 left-4 right-4 flex justify-between items-start z-10">
                <div
                  className="px-3 py-1 rounded-2xl font-bold text-xs mt-2"
                  style={{ backgroundColor: getStatusColors().bg, color: getStatusColors().text, borderColor: getStatusColors().stroke, borderWidth: 1 }}
                >
                  {getStatusLabel()}
                </div>
                {activeProduct === 'chocolateFictional' && verificationStatus !== 'suspected' && (
                  <div className="flex items-center justify-center w-16 h-16 rounded-full bg-white p-0">
                    <img 
                      src={FairtradeLogo} 
                      alt="Fairtrade Certified" 
                      className="w-full h-full object-contain"
                    />
                  </div>
                )}
              </div>
              <img src={authenticatedImage} alt="Authenticated Product" className="w-full h-auto object-contain" />
              {/* <div
                className="absolute bottom-2 left-4 px-3 py-1 rounded-lg text-sm text-left text-white z-10"
                style={{ backgroundColor: 'rgba(22, 22, 31, 0.7)' }}
              >
                <p className="font-semibold text-lg">{productConfig?.displayName || 'Product'}</p>
                <p className="font-regular text-xs pb-2">{productConfig?.name || ''}</p>
              </div> */}
            </div>
          </div>

          {/* Verified Label and Product Details */}
          <div className="flex flex-col w-full">
            {/* Verification Label */}
            <div className="flex flex-row items-center justify-start text-left px-4 py-4 w-full h-auto rounded-lg gap-4"
              style={{ backgroundColor: getStatusColors().bg, color: getStatusColors().text, borderColor: getStatusColors().stroke, borderWidth: 1 }}>
              <div 
                className="flex items-center justify-center w-16 h-16 rounded-full flex-shrink-0"
                style={{ 
                  backgroundColor: getStatusColors().stroke,
                  animation: verificationStatus === 'verified' ? 'glow-pulse-green 2s ease-in-out 4' : 
                            verificationStatus === 'suspected' ? 'glow-pulse-red 2s ease-in-out 4' :
                            verificationStatus === 'expired' ? 'glow-pulse-expired 2s ease-in-out 4' : 'none'
                }}
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-full"
                  style={{ backgroundColor: getStatusColors().iconBg }}>
                  {getStatusIcon()}
                </div>
              </div>
              <div className="text-left flex-1">
                <p className="font-bold">{verificationState?.title || 'Verified Product'}</p>
                <p className="font-regular text-sm">{verificationState?.message || 'This product is authentic and registered'}</p>
                {(urlSerialNumber || productConfig?.serialNumber) && (
                  <p className="text-xs mt-1 opacity-70">S/N: {urlSerialNumber || productConfig?.serialNumber}</p>
                )}
              </div>
            </div>

            {/* Product Details */}
            <div className="flex flex-col items-start justify-start text-left px-4 py-4 w-full mt-4 rounded-lg gap-4"
              style={{ backgroundColor: theme.background2, borderColor: theme.stroke, borderWidth: 1}}>
              <div className="border-b w-full pb-2 mb-2" style={{ borderColor: theme.stroke }}>
                <h3 className="font-bold" style={{ color: theme.text }}>Product Details</h3>
              </div>
              
              {/* Dynamic Product Data */}
              {productData && productData.content && (() => {
                const serial = urlSerialNumber || productConfig?.serialNumber;
                const overrides = (serial && productData.serialOverrides?.[serial]) || {};
                return productData.content.map((item, index) => {
                  const displayValue = overrides[item.label] !== undefined ? overrides[item.label] : item.value;
                  if (item.type === 'video') {
                    return (
                      <div
                        key={index}
                        className={index < productData.content.length - 1 ? "border-b w-full pb-4 mb-2" : ""}
                        style={{ borderColor: theme.stroke }}
                      >
                        <p className="text-sm" style={{ color: theme.gray }}>{item.label}:</p>
                        <button
                          type="button"
                          onClick={() => setActiveVideo(item)}
                          className="font-regular underline text-left"
                          style={{ color: theme.button }}
                        >
                          {displayValue}
                        </button>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={index}
                      className={index < productData.content.length - 1 ? "border-b w-full pb-4 mb-2" : ""}
                      style={{ borderColor: theme.stroke }}
                    >
                      <p className="text-sm" style={{ color: theme.gray }}>{item.label}:</p>
                      <p className="font-regular" style={{ color: theme.text }}>{displayValue}</p>
                    </div>
                  );
                });
              })()}

              {/* Loading state */}
              {!productData && (
                <div className="w-full text-center py-4">
                  <p className="text-sm" style={{ color: theme.subText }}>Loading product details...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Toggle Button */}
      <div
        className="fixed bottom-20 left-0 right-0 z-40 flex justify-center md:hidden"
      >
        <button
          onClick={() => {
            const scrollContainer = document.querySelector('.overflow-y-auto');
            if (scrollContainer) {
              if (isScrolledDown) {
                // Scroll to top
                scrollContainer.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              } else {
                // Scroll to bottom
                scrollContainer.scrollTo({
                  top: scrollContainer.scrollHeight,
                  behavior: 'smooth'
                });
              }
            }
          }}
          className="py-2 px-4 hover:opacity-80 transition-opacity"
          style={{ 
            color: theme.subText, 
            borderColor: theme.stroke,
            animation: isScrolledDown ? 'bounce-up 2s ease-in-out infinite' : 'bounce-down 2s ease-in-out infinite'
          }}
        >
          {isScrolledDown ? (
            <ChevronsUp size={20} className="mx-auto" />
          ) : (
            <ChevronsDown size={20} className="mx-auto" />
          )}
        </button>
      </div>

      {activeVideo && (
        <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0, 0, 0, 0.8)', pointerEvents: 'auto' }}>
          <div className="relative z-10 w-full max-w-3xl rounded-lg overflow-hidden" style={{ backgroundColor: theme.background2, pointerEvents: 'auto' }}>
            <div className="flex items-center justify-between p-4">
              <h2 className="font-semibold" style={{ color: theme.text }}>{activeVideo.label}</h2>
              <button
                type="button"
                onClick={() => setActiveVideo(null)}
                className="p-1"
                aria-label="Close video"
                style={{ color: theme.gray }}
              >
                <XCircle size={24} />
              </button>
            </div>
            <div className="w-full aspect-video">
              <iframe
                className="relative z-10 w-full h-full"
                src={`${activeVideo.videoUrl}&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
                title={activeVideo.label}
                referrerPolicy="origin"
                loading="eager"
                style={{ pointerEvents: 'auto', touchAction: 'manipulation' }}
                allow="autoplay; encrypted-media; fullscreen; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <a
              href={`https://www.youtube.com/watch?v=${activeVideo.videoId}&t=2s`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium"
              style={{ color: theme.button, borderTop: `1px solid ${theme.stroke}` }}
            >
              <ExternalLink size={16} />
              Watch on YouTube
            </a>
          </div>
        </div>
      )}

      {/* Bottom Navigation */}
      <BottomNavbar theme={theme} isDark={isDark} />

      {/* Animation Styles */}
      <style>{`
        @keyframes glow-pulse-green {
          0%, 100% {
            opacity: 1;
            box-shadow: 0 0 0 0 rgba(110, 231, 183, 0.4);
          }
          50% {
            opacity: 0.8;
            box-shadow: 0 0 20px 8px rgba(110, 231, 183, 0.6);
          }
        }

        @keyframes glow-pulse-expired {
          0%, 100% {
            opacity: 1;
            box-shadow: 0 0 0 0 rgba(252, 211, 77, 0.4);
          }
          50% {
            opacity: 0.8;
            box-shadow: 0 0 20px 8px rgba(252, 211, 77, 0.6);
          }
        }

        @keyframes glow-pulse-red {
          0%, 100% {
            opacity: 1;
            box-shadow: 0 0 0 0 rgba(252, 165, 165, 0.4);
          }
          50% {
            opacity: 0.8;
            box-shadow: 0 0 20px 8px rgba(252, 165, 165, 0.6);
          }
        }

        @keyframes bounce-down {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(4px);
          }
        }

        @keyframes bounce-up {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-4px);
          }
        }
      `}</style>
    </div>
  );

}

export default VerificationScreen;